import React from 'react';
import {
  Button, Input, Label, Pagination, PaginationItem, PaginationLink,
  Modal, ModalBody, FormGroup
} from "reactstrap";
import { useTranslation } from "react-i18next";
import { useSelector } from 'react-redux';
import Select from "react-select";

const RecursiveEditor = React.memo(({ value = '', path = [], onChange, onRemove = () => {}, name, keys, readOnly = false }) => {
  const { t, i18n } = useTranslation();
  const isObject = value && typeof value === "object" && !Array.isArray(value);
  const isArray = Array.isArray(value);

  const { LegalRank, LawStatus, ArticleStatus, InstrumentType, LawDocuments } = useSelector((state) => ({
    LegalRank: state.LegalRank.legalRank,
    LawStatus: state.LawReport.lawStatus,
    ArticleStatus: state.LawReport.lawStatus,
    InstrumentType: state.LawReport.instrumentType,
    LawDocuments: state.ArticleActionReport.lawDocuments,
  }));

  const options = [
    { value: "غير مذكور", label: "غير مذكور" },
    ...(LawDocuments || []).map((d) => {
      const parts = [d.DocumentType, d.GazetteNumber, d.PublicationDate]
        .filter(Boolean)
        .join(" ");

      return {
        value: d.DocNumber,
        label: `${d.DocNumber}${parts ? " (" + parts + ")" : ""}`,
      };
    }),
  ];

  const handlePrimitiveChange = (newVal) => {
    onChange(path, newVal, name);
  };

  if (isObject) {
    const objectKeys = keys ? Object.keys(value).filter((k) => !keys.includes(k)) : Object.keys(value);
    return (
      <div style={{ borderLeft: "2px solid #eee", paddingLeft: 12, marginBottom: 8 }}>
        {name && <div style={{ fontWeight: 600, marginBottom: 6 }}>{t(name)}</div>}
        {objectKeys.map((key) => (
          <RecursiveEditor
            key={key}
            keys={keys}
            name={key}
            value={value[key]}
            path={[...path, key]}
            onChange={onChange}
            onRemove={onRemove}
            readOnly={readOnly}
          />
        ))}
      </div>
    );
  }

  else if (isArray) {
    // 🧱 Default array rendering (non-"articles")
    return (
      <div style={{ borderLeft: "2px dashed #eee", paddingLeft: 12, marginBottom: 8 }}>
        <div className="d-flex justify-content-between align-items-center mb-2">
          <div style={{ fontWeight: 600, marginBottom: 6 }}>
            {t(name)} ({t('length')}: {value.length})
          </div>
          {!readOnly && (
            <Button
              size="sm"
              color="primary"
              outline
              onClick={() => {
                const newArr = [...value, typeof value[0] === "object" ? value[0] : ""];
                onChange(path, newArr, name);
              }}
            >
              {t('AddNew')}
            </Button>
          )}
        </div>
        {value.map((item, idx) => (
          <div key={idx} style={{ marginBottom: 8 }}>
            <RecursiveEditor
              keys={keys}
              name={`[${idx}]`}
              value={item}
              path={[...path, idx]}
              onChange={onChange}
              onRemove={onRemove}
              readOnly={readOnly}
            />
            {!readOnly && (
                <Button
                  size="sm"
                  color="danger"
                  outline
                  onClick={() => {
                    const newArr = [...value];
                    newArr.splice(idx, 1);
                    onChange(path, newArr, name);
                  }}
                >
                  {t('RemoveDocument')}
                </Button>
            )}
          </div>
        ))}
      </div>
    );
  }

  else if (name === "document_type") {
    return (
      <FormGroup style={{ marginBottom: 8 }}>
        {/* <Label style={{ fontWeight: 600, fontSize: 14 }}>{t(name)}</Label> */}
        <div className="d-flex justify-content-between flex-wrap my-1">
          <Label style={{ fontWeight: 600, fontSize: 14 }}>{t(name)}</Label> 
          { path.includes("article_actions") && !["target_law_number", "target_article_number", "action_description", "document_id"].includes(name) ?
            <Button size="sm" color="danger" outline 
              onClick={() => onRemove(path)}>{t('Remove')}</Button> : null
          }
        </div>
        <Input
          type="select"
          className="form-select mb-3"
          name={name}
          value={value}
          onChange={(e) => handlePrimitiveChange(e.target.value)}
          disabled={readOnly}
        >
          <option value="">{t("Select")}</option>
          {LegalRank?.map((type) => (
            <option value={type.DocTypeAr} key={type.ID}>
              {i18n.language === 'en' ? `${type.DocTypeEn} (${type.LegalRankEn})` : `${type.DocTypeAr} (${type.LegalRankAr})`}
            </option>
          ))}
        </Input>
      </FormGroup>
    )
  }

  else if (name === "document_status_normalized") {
    const v = value ? value.charAt(0).toUpperCase() + value.slice(1).toLowerCase() : value;

    return (
      <FormGroup style={{ marginBottom: 8 }}>
        {/* <Label style={{ fontWeight: 600, fontSize: 14 }}>{t(name)}</Label> */}
        <div className="d-flex justify-content-between flex-wrap my-1">
          <Label style={{ fontWeight: 600, fontSize: 14 }}>{t(name)}</Label> 
          { path.includes("article_actions") && !["target_law_number", "target_article_number", "action_description", "document_id"].includes(name) ?
            <Button size="sm" color="danger" outline 
              onClick={() => onRemove(path)}>{t('Remove')}</Button> : null
          }
        </div>
        <Input
          type="select"
          className="form-select mb-3"
          name={name}
          value={v}
          onChange={(e) => handlePrimitiveChange(e.target.value)}
          disabled={readOnly}
        >
          <option value="">{t("Select")}</option>
          {LawStatus?.map((status) => (
            <option value={`${status.NameEn.charAt(0).toUpperCase() + status.NameEn.slice(1).toLowerCase()}`} key={status.ID}>
              {i18n.language === 'en' ? status.NameEn : status.NameAr}
            </option>
          ))}
        </Input>
      </FormGroup>
    )
  }

  else if (name === "article_status") {
    const v = value ? value.charAt(0).toUpperCase() + value.slice(1).toLowerCase() : value;

    return (
      <FormGroup style={{ marginBottom: 8 }}>
        {/* <Label style={{ fontWeight: 600, fontSize: 14 }}>{t(name)}</Label> */}
        <div className="d-flex justify-content-between flex-wrap my-1">
          <Label style={{ fontWeight: 600, fontSize: 14 }}>{t(name)}</Label> 
          { path.includes("article_actions") && !["target_law_number", "target_article_number", "action_description", "document_id"].includes(name) ?
            <Button size="sm" color="danger" outline 
              onClick={() => onRemove(path)}>{t('Remove')}</Button> : null
          }
        </div>
        <Input
          type="select"
          className="form-select mb-3"
          name={name}
          value={v}
          onChange={(e) => handlePrimitiveChange(e.target.value)}
          disabled={readOnly}
        >
          <option value="">{t("Select")}</option>
          {ArticleStatus?.map((status) => (
            <option value={`${status.NameEn.charAt(0).toUpperCase() + status.NameEn.slice(1).toLowerCase()}`} key={status.ID}>
              {i18n.language === 'en' ? status.NameEn : status.NameAr}
            </option>
          ))}
        </Input>
      </FormGroup>
    )
  }

  else if (name === "instrument_type") {
    return (
      <FormGroup style={{ marginBottom: 8 }}>
        {/* <Label style={{ fontWeight: 600, fontSize: 14 }}>{t(name)}</Label> */}
        <div className="d-flex justify-content-between flex-wrap my-1">
          <Label style={{ fontWeight: 600, fontSize: 14 }}>{t(name)}</Label> 
          { path.includes("article_actions") && !["target_law_number", "target_article_number", "action_description", "document_id"].includes(name) ?
            <Button size="sm" color="danger" outline 
              onClick={() => onRemove(path)}>{t('Remove')}</Button> : null
          }
        </div>
        <Input
          type="select"
          className="form-select mb-3"
          name={name}
          value={value}
          onChange={(e) => handlePrimitiveChange(e.target.value)}
          disabled={readOnly}
        >
          <option value="">{t("Select")}</option>
          {InstrumentType?.map((type) => (
            <option value={type.NameAr} key={type.ID}>
              {i18n.language === 'en' ? type.NameEn : type.NameAr}
            </option>
          ))}
        </Input>
      </FormGroup>
    )
  }

  else if (name === "target_law_number") {
    return (
      <FormGroup style={{ marginBottom: 8 }}>
        {/* <Label style={{ fontWeight: 600, fontSize: 14 }}>{t(name)}</Label> */}
        <div className="d-flex justify-content-between flex-wrap my-1">
          <Label style={{ fontWeight: 600, fontSize: 14 }}>{t(name)}</Label> 
          { path.includes("article_actions") && !["target_law_number", "target_article_number", "action_description", "document_id"].includes(name) ?
            <Button size="sm" color="danger" outline 
              onClick={() => onRemove(path)}>{t('Remove')}</Button> : null
          }
        </div>
        <Select
          className="wrap-text mb-3"
          classNamePrefix="react-select"
          name={name}
          value={options.find((opt) => opt.value === value) || null}
          onChange={(selected) => handlePrimitiveChange(selected ? selected.value : "")}
          options={options}
          placeholder={value}
          isDisabled={readOnly}
        />
      </FormGroup>
    )
  }

  else if (name === "IsReplaced" && !value) {
    return null;
  }

  else {
    const asString = value === null ? "" : String(value);
    const useTextarea = asString.length > 60 || asString.includes("\n");

    return (
      <FormGroup style={{ marginBottom: 8 }}>
        <div className="d-flex justify-content-between flex-wrap my-1">
          <Label style={{ fontWeight: 600, fontSize: 14 }}>{t(name)}</Label> 
          { path.includes("article_actions") && !["target_law_number", "target_article_number", "action_description", "document_id"].includes(name) ?
            <Button size="sm" color="danger" outline 
              onClick={() => onRemove(path)}>{t('Remove')}</Button> : null
          }
        </div>
        {useTextarea ? (
          <Input
          rows={5}
            type="textarea"
            value={asString}
            placeholder={name === 'new_law_number' ? 'xx/xxxx' : name == 'document_id' ? '' : t('Enter value here...')}
            onChange={(e) => handlePrimitiveChange(e.target.value)}
            readOnly={readOnly || name == 'document_id'}
          />
        ) : (
          <Input
            type="text"
            value={asString}
            placeholder={name === 'new_law_number' ? 'xx/xxxx' : name == 'document_id' ? '' : t('Enter value here...')}
            onChange={(e) => handlePrimitiveChange(e.target.value)}
            readOnly={readOnly || name == 'document_id'}
          />
        )}
      </FormGroup>
    );
  }
})

export function updateAtPath(obj, path, newValue) {
  if (path.length === 0) {
    return newValue;
  }
  const [head, ...rest] = path;
  let clone;
  if (Array.isArray(obj)) clone = [...obj];
  else if (obj && typeof obj === "object") clone = { ...obj };
  else {
    clone = typeof head === "number" ? [] : {};
  }

  clone[head] = updateAtPath(clone[head], rest, newValue);
  return clone;
}

export  function attemptParsePrimitive(v) {
  // keep strings by default; but if user typed 'true'/'false'/'null'/'123' we might parse
  if (v === null) return null;
  if (typeof v !== "string") return v;
  const trimmed = v.trim();
  if (trimmed === "") return "";
  if (trimmed === "null") return null;
  if (trimmed === "true") return true;
  if (trimmed === "false") return false;
  if (!isNaN(Number(trimmed)) && trimmed.length < 16) {
    return Number(trimmed);
  }
  return v;
}

export const fields = {
  "LawTitle": { path: ["document_info", "law_title"] },
  "DocumentType": { path: ["document_info", "document_type"] },
  "LawNumber": { path: ["document_info", "law_number"] },
  "GazetteNo": { path: ["document_info", "gazette_number"] },
  "PublicationDate": { path: ["document_info", "publication_date"] },
  "DocNumber": { path: ["document_info", "document_number"] },
  "DocumentStatusNormalized": { path: ["document_info", "document_status_normalized"] }
}

const LawDocument = ({ show, setShow, selectedRow, setSelectedRow, fieldKey,  onSaveClickHandler, disabled }) => {
  const { t, i18n } = useTranslation();

  const { LawDocuments } = useSelector((state) => ({
    LawDocuments: state.ArticleActionReport.lawDocuments,
  }));

  const handleSelectChange = (e, index) => {
    const { name, value } = e.target;

    setSelectedRow(prev => {
      const oldValue = prev[name][index];   // old instrument_type

      // 1. Update unique list
      const updatedInstrumentTypes = prev[name].map((item, idx) =>
        idx === index ? value : item
      );

      // 2. Update ALL articles whose instrument_type matches oldValue
      const updatedArticles = prev.Json.articles?.map(article => ({
        ...article,
        instrument_type:
          article.instrument_type === oldValue ? value : article.instrument_type
      }))

      return {
        ...prev,
        [name]: updatedInstrumentTypes,
        Json: {
          ...prev.Json,
          articles: updatedArticles
        }
      };
    });
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    const path = fields[name]?.path || [];
    
    const updated = updateAtPath(selectedRow.Json, path, value);

    setSelectedRow((prev) => ({
      ...prev, 
      [name]: value,
      Json: updated
    }));
  };

  const onChangeValue = (path, newVal, name) => {
    const parsedVal = attemptParsePrimitive(newVal);
    let updatedBase = updateAtPath(selectedRow, [...path], parsedVal);

    if (name == 'target_law_number' && newVal === 'غير مذكور') {
      const updatedPath = path.slice(0, -1);

      updatedBase = updateAtPath(updatedBase, [...updatedPath, "new_law_number"], '');
      updatedBase = updateAtPath(updatedBase, [...updatedPath, "document_id"], 'N/A');

    } else if (name == 'target_law_number') {
      const updatedPath = path.slice(0, -1);
      updatedPath.push('document_id');

      const document = LawDocuments.find((item) => item.DocNumber == parsedVal);
      if (document) {
        updatedBase = updateAtPath(updatedBase, [...updatedPath], document.ID);
      }
    }

    const updated = {
      ...updatedBase,
      Json: {
        ...updatedBase.Json,
        articles: (updatedBase.Json?.articles || []).map((article, index) =>
          index == updatedBase.Index
        ? { ...article, article_actions: updatedBase.article_actions }
        : article
      )},
    };
  
    setSelectedRow(updated)
  };

  const onRemoveValue = (path) => {
    if (!Array.isArray(path) || path.length === 0) return;

    // ✅ Last key is ALWAYS the thing to remove
    const keyToRemove = path[path.length - 1];

    // ✅ Parent is everything before it
    const parentPath = path.slice(0, -1);

    // ✅ Read the parent object safely
    const parentObj = parentPath.reduce(
      (acc, k) => (acc ? acc[k] : undefined),
      selectedRow
    );

    if (!parentObj) return;

    let updatedParent;

    // ✅ ARRAY → remove by index
    if (Array.isArray(parentObj)) {
      const idx = Number(keyToRemove);
      if (Number.isNaN(idx)) return;

      updatedParent = [...parentObj];
      updatedParent.splice(idx, 1);
    }
    // ✅ OBJECT → remove by key
    else if (typeof parentObj === "object") {
      updatedParent = { ...parentObj };
      delete updatedParent[keyToRemove];
    } 
    else {
      return;
    }

    // ✅ Apply using your updateAtPath
    const updatedBase = updateAtPath(
      selectedRow,
      parentPath,
      updatedParent
    );

    // ✅ Keep your article sync logic
    const updated = {
      ...updatedBase,
      Json: {
        ...updatedBase.Json,
        articles: (updatedBase.Json?.articles || []).map((article, index) =>
          index === updatedBase.Index
            ? { ...article, article_actions: updatedBase.article_actions }
            : article
        ),
      },
    };

    setSelectedRow(updated);
  };
  
  const {legalRank, lawStatus, instrumentType } = useSelector(
    (state) => ({
      legalRank: state.LegalRank.legalRank,
      lawStatus: state.LawReport.lawStatus,
      instrumentType: state.LawReport.instrumentType
    })
  );

  return (
    show ?
    <Modal isOpen={show} centered={true}>
      <ModalBody className="py-3 px-5">
        <div className="mt-2 text-start">
          <div className="mt-4 pt-2 fs-15 mx-4 mx-sm-5">
            <label htmlFor={fieldKey} className="col-form-label">{t(fieldKey)}<small className="text-danger"> *</small></label>
            { ['repeals', 'replaces', 'adds_new_article', 'adds_paragraph', 'amends_text_in', 'renumbers', 'renames_element'].includes(fieldKey) ? 
              <>
                <RecursiveEditor
                  value={selectedRow.article_actions[fieldKey] || []}
                  path={selectedRow.Path || []}
                  onChange={onChangeValue} 
                  onRemove={onRemoveValue}
                /> 
              </> :
             ['LawTitle', 'LawNumber', 'DocNumber', 'GazetteNo', 'PublicationDate'].includes(fieldKey)  ?
              <Input
                type="textarea"
                row="2"
                className="form-control"
                placeholder={t('Enter value here...')}
                value={selectedRow[fieldKey] || ''}
                name={fieldKey}
                onChange={handleChange}
                required
              /> : 
              fieldKey === 'DocumentType' ?
              <Input
                type="select"
                className="form-select"
                name={fieldKey}
                value={(selectedRow[fieldKey] || '').trim()}
                onChange={handleChange}
                required
              >
                <option value="">{t("Select")}</option>
                {legalRank?.map((type) => (
                  <option value={type.DocTypeAr} key={type.ID}>
                    {i18n.language === 'en' ? `${type.DocTypeEn} (${type.LegalRankEn})` : `${type.DocTypeAr} (${type.LegalRankAr})`}
                  </option>
                ))}
              </Input> :
              fieldKey === 'DocumentStatusNormalized' ?
              <Input
                type="select"
                className="form-select"
                name={fieldKey}
                value={(selectedRow[fieldKey] || '').trim()}
                onChange={handleChange}
                required
              >
                <option value="">{t("Select")}</option>
                {lawStatus?.map((status) => (
                  <option value={`${status.NameEn.charAt(0).toUpperCase() + status.NameEn.slice(1).toLowerCase()}`} key={status.ID}>
                    {i18n.language === 'en' ? status.NameEn : status.NameAr}
                  </option>
                ))}
              </Input> :
              fieldKey === 'InstrumentType' && Array.isArray(selectedRow[fieldKey]) ? 
              Array.isArray(selectedRow[fieldKey]) && selectedRow[fieldKey]?.map((instType, index) => {
                return (
                  <Input
                    key={index}
                    type="select"
                    className="form-select mb-3"
                    name={fieldKey}
                    value={(instType || '').trim()}
                    onChange={(e) => handleSelectChange(e, index)}
                    required
                  >
                    <option value="">{t("Select")}</option>
                    {instrumentType?.map((type) => (
                      <option value={type.NameAr} key={type.ID}>
                        {i18n.language === 'en' ? type.NameEn : type.NameAr}
                      </option>
                    ))}
                  </Input>
                )
              })
              : null
              }
          </div>
        </div>
        <div className="d-flex gap-2 justify-content-center mt-4 mb-2">
          <button
            type="button"
            className="btn w-sm btn-light"
            data-bs-dismiss="modal"
            onClick={() => setShow(false)}
          >
            {t("Close")}
          </button>
          <button
            type="button"
            className="btn w-sm btn-danger d-flex align-items-center"
            disabled={disabled}
            onClick={onSaveClickHandler}
          >
            {t('Yes, Save it')}
          </button>
        </div>
      </ModalBody>
    </Modal> : null
  )
}

export default React.memo(LawDocument)